<template>
  <div>
    <div v-if="loading" class="text-muted py-4">Carregando consultas anteriores...</div>
    <div v-else-if="error" class="text-danger py-4">{{ error }} <button type="button" class="btn btn-link" @click="load">Tentar novamente</button></div>
    <template v-else>
      <template v-if="signingLatest && latestIssued">
        <p>Será criada uma nova cópia de {{ latestIssued.name }} ({{ latestIssued.date }}). O documento original permanecerá intacto.</p>
        <SelectCertifier :id="'reissue_' + kind + '_' + recordId + '_' + (taskContext?.id || 'attendance')"
          inline :certifiers="certifiers" :disabled="saving" @onSelect="copy" />
        <p v-if="saving" role="status">Preparando nova cópia para assinatura...</p>
        <button type="button" class="btn btn-light-primary reissue-latest mb-3" :disabled="saving" @click="taskContext?.quick ? $emit('modify') : signingLatest = false">
          <i class="bi bi-pencil" aria-hidden="true"></i>
          <span>{{ kind === 'prescriptions' ? 'Modificar última prescrição' : (kind === 'exam-requests' ? 'Modificar último pedido de exame' : 'Modificar último documento') }}</span>
        </button>
      </template>
      <template v-else>
      <button v-if="latestIssued" type="button" class="btn btn-light-primary reissue-latest mb-3" :disabled="saving" @click="selectLatest">
        <i class="bi bi-arrow-repeat" aria-hidden="true"></i>
        <span>{{ kind === 'prescriptions' ? 'Reemitir última prescrição' : (kind === 'exam-requests' ? 'Reemitir último pedido de exame' : 'Usar último documento') }}</span>
      </button>
      <label class="form-label d-block">Consulta e documento</label>
      <SelectUbbi v-if="agendaStyle" v-model="selectedId" :options="documentOptions"
                  :disabled="saving" placeholder="Selecione um documento anterior"
                  searchable @change="choose" />
      <select v-else class="form-select mb-4" v-model="selectedId" :disabled="saving" @change="choose">
        <option :value="null">Selecione um documento anterior</option>
        <option v-for="document in documents" :key="document.id" :value="document.id">
          {{ document.date }} · {{ document.name }} · {{ document.items.map(item => item.label).join(', ').slice(0, 100) }}
        </option>
      </select>
      <p v-if="!documents.length" class="text-muted">Nenhum documento anterior disponível para copiar.</p>
      <div v-if="selected">
        <p class="text-muted">Selecione o que deseja aproveitar. A cópia será aberta para revisão antes de finalizar.</p>
        <label v-for="item in selected.items" :key="item.id" class="d-flex gap-3 border-bottom py-3">
          <input type="checkbox" class="form-check-input" :value="item.id" v-model="itemIds" :disabled="saving">
          <span style="white-space: pre-wrap">{{ item.label }}</span>
        </label>
        <p v-if="selected.observation" class="mt-3" style="white-space: pre-wrap">Observações: {{ selected.observation }}</p>
        <button type="button" class="btn btn-primary mt-4" :disabled="saving || !itemIds.length" @click="copy()">
          {{ saving ? 'Criando...' : 'Criar cópia' }}
        </button>
      </div>
      </template>
    </template>
  </div>
</template>

<script>
import SelectCertifier from "@/components/SelectCertificator.vue";
import SelectUbbi from "@/components/SelectUbbi.vue";
import moment from 'moment';
import http from '../../../helpers/http';
import Alert from '../../../helpers/alert';

export default {
  components: {SelectUbbi, SelectCertifier},
  props: {
    agendaStyle: Boolean,
    taskContext: { type: Object, default: null },
    professionalId: { required: true },
    patientId: { required: true },
    recordId: { required: true },
    kind: { type: String, required: true },
  },
  emits: ['created', 'empty', 'modify'],
  data() { return { loading: false, signingLatest: false, certifiers: [], saving: false, error: '', documents: [], selectedId: null, itemIds: [] }; },
  computed: {
    latestIssued() { return this.documents.find(document => document.generated); },
    documentOptions() {
      return this.documents.map(document => ({
        value: document.id,
        label: `${document.date} · ${document.name} · ${document.items.map(item => item.label).join(', ').slice(0, 100)}`,
      }));
    },
    selected() { return this.documents.find(document => document.id === this.selectedId); },
    baseUrl() { return `/professionals/${this.professionalId}/patients/${this.patientId}/medical-records`; },
  },
  mounted() { this.load(); },
  methods: {
    selectLatest() {
      this.selectedId = this.latestIssued.id;
      this.choose();
      this.signingLatest = true;
    },
    async load() {
      this.loading = true;
      this.error = '';
      try {
        const [{ data }, certificates] = await Promise.all([http.get(this.baseUrl), http.get('/digital-certificates/certifiers')]);
        this.certifiers = certificates.data.filter(certifier => certifier.id !== 'none');
        const prescription = this.kind === 'prescriptions';
        const textDocument = this.kind === 'documents';
        const key = textDocument ? 'documents' : (prescription ? 'prescriptions' : 'exam_requests');
        this.documents = data.filter(record => (this.taskContext || Number(record.id_medrec) !== Number(this.recordId))
            && Number(record.id_patient) === Number(this.patientId)
            && (!this.taskContext || (Number(record.id_place) === Number(this.taskContext.placeId)
              && Number(record.id_prof) === Number(this.professionalId))))
          .flatMap(record => (record[key] || []).filter(document => !this.taskContext || Number(document.id_task_origin) !== Number(this.taskContext.id))
            .map(document => ({
              id: textDocument ? document.id_document : (prescription ? document.id_presc : document.id_exam_request),
              timestamp: document.created_at || record.medrec_date_time,
              date: moment(document.created_at || record.medrec_date_time).format('DD/MM/YYYY HH:mm'),
              generated: textDocument ? document.document_generated : (prescription ? document.presc_request_generated : document.exam_request_generated),
              name: (textDocument ? document.document_name : (prescription ? document.presc_name : document.exam_request_name)) || 'Documento',
              observation: prescription ? document.presc_obs : document.exam_request_obs,
              items: textDocument ? [{ id: 0, label: this.contentText(document.document_body) }]
                : prescription && document.content_type && document.content_type !== 'medication'
                  ? [{ id: 0, label: this.contentText(document.presc_payload) }]
                  : (document[prescription ? 'drugs' : 'exams'] || []).map(item => ({
                    id: prescription ? item.id_presc_drug : item.id_exam,
                    label: prescription ? [item.drug_name, item.concentration, item.pharmaceutical_form, item.dosage_desc,
                      item.quantity ? `Quantidade: ${item.quantity}` : '', item.treatment_time, item.drug_obs].filter(Boolean).join(' · ') : item.exam_name,
                  })),
            }))).filter(document => document.items.length)
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp) || b.id - a.id);
        if (this.taskContext?.quick && this.latestIssued) this.selectLatest();
        if (this.taskContext && !this.latestIssued) this.$emit('empty');
      } catch (e) {
        this.error = 'Não foi possível carregar as consultas anteriores.';
      } finally { this.loading = false; }
    },
    contentText(value) {
      if (value == null) return '';
      if (typeof value !== 'object') return String(value).replace(/<[^>]*>/g, '');
      return Object.entries(value).map(([key, item]) => `${['text', 'items'].includes(key) ? '' : key + ': '}${this.contentText(item)}`).join('\n');
    },
    choose() { this.itemIds = this.selected?.items.map(item => item.id) || []; },
    async copy(certifier = null) {
      if (this.saving || !this.itemIds.length) return;
      this.saving = true;
      try {
        const { data } = await http.post(`${this.baseUrl}/${this.recordId}/${this.kind}`, {
          ...(this.taskContext ? { task_id: this.taskContext.id, task_standalone: true } : {}),
          id_medrec: this.recordId,
          source_document_id: this.selectedId,
          selected_item_ids: this.itemIds,
        });
        if (!certifier) Alert.toastSuccess('Cópia criada. Revise os itens antes de finalizar.');
        this.$emit('created', { document: data, certifier });
      } catch (e) {
        Alert.toastError(e.response?.data?.message || 'Não foi possível criar a cópia.');
      } finally { this.saving = false; }
    },
  },
};
</script>

<style scoped>
.reissue-latest { display: inline-flex; align-items: center; gap: 8px; min-height: 38px; padding: 9px 14px; border-radius: 8px; font-size: 13px; font-weight: 600; line-height: 1.4; text-align: left; }
.reissue-latest i { padding: 0; flex-shrink: 0; font-size: 16px; }
.reissue-latest:focus-visible { outline: 3px solid #ba83ad; outline-offset: 2px; }
</style>
